/**
 * Blog Database Schema for SurrealDB
 *
 * This file defines the database schema for the blog system.
 * Database: blog (within toolverse namespace)
 *
 * i18n model: flat `field_{locale}` columns per row.
 * Locales: en, de, es, fr, it, ja, nl, pt_br, pt, zh
 */

const LOCALES = [
  "en",
  "de",
  "es",
  "fr",
  "it",
  "ja",
  "nl",
  "pt_br",
  "pt",
  "zh",
] as const;

export type BlogLocale = (typeof LOCALES)[number];

/**
 * Map Paraglide locale codes to SurrealDB-safe field suffixes.
 *
 * SECURITY: the result is interpolated into query identifiers (`slug_${locale}`,
 * `title_${locale}`, …), so it MUST be a known-safe value. Any unrecognized
 * locale falls back to "en" rather than passing attacker-controlled text into
 * the query string.
 */
export function toDbLocale(paraglideLocale: string): BlogLocale {
  const candidate = paraglideLocale === "pt-BR" ? "pt_br" : paraglideLocale;
  return (LOCALES as readonly string[]).includes(candidate)
    ? (candidate as BlogLocale)
    : "en";
}

/** Map SurrealDB field suffix back to Paraglide locale code */
export function toParaglideLocale(dbLocale: BlogLocale): string {
  if (dbLocale === "pt_br") return "pt-BR";
  return dbLocale;
}

export { LOCALES as BLOG_LOCALES };

function localeFields(
  tableName: string,
  fieldName: string,
  type: string,
): string {
  return LOCALES.map(
    (l) =>
      `DEFINE FIELD IF NOT EXISTS ${fieldName}_${l} ON ${tableName} TYPE ${type};`,
  ).join("\n");
}

function localeIndexes(
  tableName: string,
  fieldName: string,
  unique = false,
): string {
  return LOCALES.map(
    (l) =>
      `DEFINE INDEX IF NOT EXISTS ${tableName}_${fieldName}_${l}_idx ON ${tableName} FIELDS ${fieldName}_${l}${unique ? " UNIQUE" : ""};`,
  ).join("\n");
}

export const BLOG_SCHEMA = `
-- ============================================================================
-- Blog Database Schema for SurrealDB
-- ============================================================================

-- ============================================================================
-- blog_category
-- ============================================================================

DEFINE TABLE IF NOT EXISTS blog_category SCHEMAFULL;
DEFINE FIELD IF NOT EXISTS defaultSlug ON blog_category TYPE string;
${localeFields("blog_category", "slug", "string")}
${localeFields("blog_category", "name", "string")}
${localeFields("blog_category", "description", "option<string>")}
DEFINE FIELD IF NOT EXISTS sortOrder ON blog_category TYPE int DEFAULT 0;
-- Two-level taxonomy: a category is either a TOOL (kind="tool", parent=NONE)
-- or a TOPIC under a tool (kind="topic", parent=<tool category>).
-- Posts link to the TOPIC (leaf); the tool is derived via category.parent.
DEFINE FIELD IF NOT EXISTS kind ON blog_category TYPE string DEFAULT "topic";
DEFINE FIELD IF NOT EXISTS parent ON blog_category TYPE option<record<blog_category>>;
DEFINE FIELD IF NOT EXISTS createdAt ON blog_category TYPE datetime DEFAULT time::now();
DEFINE FIELD IF NOT EXISTS updatedAt ON blog_category TYPE datetime VALUE time::now();

DEFINE INDEX IF NOT EXISTS blog_category_defaultSlug_idx ON blog_category FIELDS defaultSlug UNIQUE;
DEFINE INDEX IF NOT EXISTS blog_category_parent_idx ON blog_category FIELDS parent;
-- Slugs are unique PER TOOL, not globally: tool #2 can have its own "strategy"
-- topic. Composite (parent, slug_<locale>) UNIQUE. Tools (parent=NONE) are
-- unique among themselves. Lookups always resolve by the (tool, topic) pair.
${LOCALES.map((l) => `DEFINE INDEX IF NOT EXISTS blog_category_parent_slug_${l}_idx ON blog_category FIELDS parent, slug_${l} UNIQUE;`).join("\n")}

-- ============================================================================
-- blog_post
-- ============================================================================

DEFINE TABLE IF NOT EXISTS blog_post SCHEMAFULL;
DEFINE FIELD IF NOT EXISTS category ON blog_post TYPE record<blog_category>;
DEFINE FIELD IF NOT EXISTS status ON blog_post TYPE string DEFAULT "draft";
DEFINE FIELD IF NOT EXISTS publishedAt ON blog_post TYPE option<datetime>;
DEFINE FIELD IF NOT EXISTS createdAt ON blog_post TYPE datetime DEFAULT time::now();
DEFINE FIELD IF NOT EXISTS updatedAt ON blog_post TYPE datetime VALUE time::now();
-- Honest modification date: set by the publish pipeline ONLY when the content
-- fingerprint (contentHash) changes. Drives sitemap <lastmod> + dateModified.
-- (updatedAt above moves on EVERY write, so it must never be exposed as lastmod.)
DEFINE FIELD IF NOT EXISTS contentHash ON blog_post TYPE option<string>;
DEFINE FIELD IF NOT EXISTS contentUpdatedAt ON blog_post TYPE option<datetime>;
${localeFields("blog_post", "slug", "option<string>")}
${localeFields("blog_post", "title", "option<string>")}
${localeFields("blog_post", "description", "option<string>")}
${localeFields("blog_post", "markdown", "option<string>")}
${localeFields("blog_post", "html", "option<string>")}
${localeFields("blog_post", "toc", "option<string>")}
DEFINE FIELD IF NOT EXISTS coverImage ON blog_post TYPE option<string>;
DEFINE FIELD IF NOT EXISTS coverImageMobile ON blog_post TYPE option<string>;
DEFINE FIELD IF NOT EXISTS readingTimeMinutes ON blog_post TYPE int DEFAULT 5;
DEFINE FIELD IF NOT EXISTS chartData ON blog_post TYPE option<string>;

${localeIndexes("blog_post", "slug")}
DEFINE INDEX IF NOT EXISTS blog_post_status_idx ON blog_post FIELDS status;
DEFINE INDEX IF NOT EXISTS blog_post_category_idx ON blog_post FIELDS category;
DEFINE INDEX IF NOT EXISTS blog_post_status_published_idx ON blog_post FIELDS status, publishedAt;

-- ============================================================================
-- blog_tag
-- ============================================================================

DEFINE TABLE IF NOT EXISTS blog_tag SCHEMAFULL;
DEFINE FIELD IF NOT EXISTS slug ON blog_tag TYPE string;
${localeFields("blog_tag", "name", "string")}
DEFINE FIELD IF NOT EXISTS createdAt ON blog_tag TYPE datetime DEFAULT time::now();
DEFINE FIELD IF NOT EXISTS updatedAt ON blog_tag TYPE datetime VALUE time::now();

DEFINE INDEX IF NOT EXISTS blog_tag_slug_idx ON blog_tag FIELDS slug UNIQUE;

-- ============================================================================
-- blog_post_tag (RELATION: blog_post -> blog_tag)
-- ============================================================================

DEFINE TABLE IF NOT EXISTS blog_post_tag SCHEMAFULL TYPE RELATION IN blog_post OUT blog_tag;
DEFINE FIELD IF NOT EXISTS createdAt ON blog_post_tag TYPE datetime DEFAULT time::now();
`;

/**
 * Initialize the blog schema in SurrealDB
 */
export async function initializeBlogSchema(
  db: import("surrealdb").Surreal,
): Promise<void> {
  console.log("[SurrealDB] Initializing blog schema...");

  try {
    await db.query(BLOG_SCHEMA);
    console.log("[SurrealDB] Blog schema initialized successfully");
  } catch (error) {
    console.error("[SurrealDB] Failed to initialize blog schema:", error);
    throw error;
  }
}
